// Déconnexion : entre le clic sur « Se déconnecter » et l'arrivée sur la page de connexion,
// l'écran ne bougeait pas du tout. Le `button_to` est en `data: { turbo: false }`, donc pas
// de barre de progression Turbo — le navigateur garde l'ancien document peint jusqu'au
// premier paint du suivant, et l'app reste là, intacte et muette, le dropdown encore ouvert.
//
// Un seul temps, contrairement au create (cf. create_submit_transition.ts) : la déconnexion
// ne revient jamais au formulaire. Il n'y a pas d'erreur de validation à attendre, pas de
// retour possible — on part, point. L'overlay est donc peint IMMÉDIATEMENT, sans le délai de
// 300 ms qui protège le create d'un squelette apparu puis disparu.
//
// Un rideau, pas un squelette : on ne sait pas de quoi la page d'arrivée aura l'air — la
// connexion a sa propre mise en page, deux colonnes avec l'image de marque, rien à voir avec
// l'app shell. Un squelette d'app shell annoncerait une page qui ne viendra pas. Le rideau
// dit la seule chose qui soit vraie : on quitte.

const OVERLAY_CLASS = 'sign-out-transition';
// `button_to` rend un <form> ; l'attribut est posé dessus via `form: { data: … }`, et non sur
// le bouton, parce que c'est le formulaire qui émet `submit`.
const FORM_SELECTOR = 'form[data-sign-out-transition]';
// Filet : si la requête n'aboutit pas (réseau coupé, 5xx), l'app ne doit pas rester voilée.
// Plus court que le filet du create — ici il n'y a qu'un DELETE et une redirection.
const SAFETY_TIMEOUT_MS = 10_000;

let safetyTimeout: number | null = null;

const remove = () => {
  if (safetyTimeout !== null) window.clearTimeout(safetyTimeout);
  safetyTimeout = null;
  document.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((node) => node.remove());
};

const paintCurtain = (label: string) => {
  if (document.querySelector(`.${OVERLAY_CLASS}`)) return;

  const overlay = document.createElement('div');
  overlay.className = OVERLAY_CLASS;
  // `role="status"` et non `aria-hidden` comme le squelette de nav : celui-ci double un
  // contenu déjà annoncé par la navigation, alors qu'ici le rideau EST la seule annonce.
  overlay.setAttribute('role', 'status');

  const spinner = document.createElement('span');
  spinner.className = `${OVERLAY_CLASS}__spinner`;
  spinner.setAttribute('aria-hidden', 'true');

  const text = document.createElement('span');
  text.className = `${OVERLAY_CLASS}__label`;
  text.textContent = label;

  overlay.append(spinner, text);
  // Sur le <body> et non dans `.app-shell__main` : le menu natif (Hotwire Native) a sa propre
  // mise en page, sans colonne principale, et le rideau doit couvrir l'écran dans les deux.
  document.body.appendChild(overlay);
};

export function initializeSignOutTransition() {
  if ((window as any).__signOutTransitionInitialized) return;
  (window as any).__signOutTransitionInitialized = true;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
      if (event.defaultPrevented) return;

      paintCurtain(form.dataset.signOutTransition || '');
      safetyTimeout = window.setTimeout(remove, SAFETY_TIMEOUT_MS);
    },
    // Capture : le rideau doit être peint même si un autre écouteur arrête la propagation
    // avant nous (le dropdown Bootstrap qui se referme, par exemple).
    true
  );

  // Retour arrière depuis le bfcache. Après une déconnexion la page authentifiée n'est
  // normalement plus servie, mais si le navigateur la restaure telle quelle, elle
  // reviendrait avec son rideau — une app voilée et inerte, sans rien pour l'expliquer.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) remove();
  });

  // Filets, comme pour les autres transitions : aucun de ces événements n'arrive sur le
  // chemin nominal (la sortie est un rechargement pleine page, pas une visite Turbo), mais
  // ils rattrapent une réponse qui emmènerait finalement ailleurs, et `turbo:before-cache`
  // garantit qu'un rideau n'entre jamais dans le snapshot de la page qu'on quitte.
  document.addEventListener('turbo:before-cache', remove);
  document.addEventListener('turbo:load', remove);
  document.addEventListener('turbo:render', remove);
}
